import { createReducer } from '@reduxjs/toolkit';
import { Nullable } from 'modules/types/common';
import * as storageActions from 'store/amwell/storage/actions';
import { RESET_AMWELL_ACTIVITY } from '../activeConsumer/actions';
import { ChatReport, VisitSummary, Visit, VisitType, VideoContext, VisitStep } from './types.web';
import * as actions from './actions.web';

export type VisitState = Readonly<{
  telehealthVideoInstallUrl: string;
  details: {
    step: Nullable<VisitStep>;
    type: Nullable<VisitType>;
    data: Nullable<Visit>;
    loading: {
      create: boolean;
      start: boolean;
      process: boolean;
      transfer: boolean;
    };
    errors: {
      create: Nullable<Error>;
      start: Nullable<Error>;
      process: Nullable<Error>;
      transfer: Nullable<Error>;
    };
  };
  coupon: {
    loading: boolean;
    error: Nullable<Error>;
  };
  summary: {
    data: Nullable<VisitSummary>;
    loading: boolean;
    error: Nullable<Error>;
  };
  alerts: {
    loading: boolean;
    error: Nullable<Error>;
  };
  chat: {
    data: Nullable<ChatReport>;
    loading: boolean;
    error: Nullable<Error>;
  };
  webRTCVideoConsole: {
    data: Nullable<VideoContext>;
    loading: boolean;
    error: Nullable<Error>;
  };
}>;

export const visitInitialState: VisitState = {
  telehealthVideoInstallUrl: '',
  details: {
    step: null,
    type: null,
    data: null,
    loading: {
      create: false,
      start: false,
      process: false,
      transfer: false
    },
    errors: {
      create: null,
      start: null,
      process: null,
      transfer: null
    }
  },
  coupon: {
    loading: false,
    error: null
  },
  summary: {
    data: null,
    loading: false,
    error: null
  },
  alerts: {
    loading: false,
    error: null
  },
  chat: {
    data: null,
    loading: false,
    error: null
  },
  webRTCVideoConsole: {
    data: null,
    loading: false,
    error: null
  }
};

export const visitReducer = createReducer(visitInitialState, {
  [actions.GET_TELEHEALTH_VIDEO_URL_SUCCESS]: (state, action) => {
    state.telehealthVideoInstallUrl = action.payload;
  },
  [actions.CREATE_VISIT]: state => {
    state.details.loading.create = true;
    state.details.errors.create = null;
  },
  [actions.CREATE_VISIT_FAIL]: (state, action) => {
    state.details.errors.create = action.error;
    state.details.loading.create = false;
  },
  [actions.CREATE_VISIT_SUCCESS]: (state, action) => {
    state.details.data = action.payload;
    state.details.loading.create = false;
  },
  [actions.CREATE_VISIT_FROM_SPEED_PASS]: state => {
    state.details.loading.create = true;
    state.details.errors.create = null;
  },
  [actions.CREATE_VISIT_FROM_SPEED_PASS_FAIL]: (state, action) => {
    state.details.errors.create = action.error;
    state.details.loading.create = false;
  },
  [actions.CREATE_VISIT_FROM_SPEED_PASS_SUCCESS]: (state, action) => {
    state.details.data = action.payload.visit;
    state.details.loading.create = false;
  },
  [actions.FIND_ACTIVE_VISIT_SUCCESS]: (state, action) => {
    state.details.data = action.payload;
  },
  [actions.START_VISIT]: state => {
    state.details.loading.start = true;
    state.details.errors.start = null;
    state.details.type = null;
    state.details.step = null;
  },
  [actions.START_VISIT_FAIL]: (state, action) => {
    state.details.errors.start = action.error;
    state.details.loading.start = false;
  },
  [actions.START_VISIT_SUCCESS]: (state, action) => {
    state.details.loading.start = false;
    state.details.data = action.payload;
  },
  [actions.PROCESS_VISIT]: state => {
    state.details.loading.process = true;
    state.details.errors.process = null;
  },
  [actions.VISIT_EMITTER]: (state, action) => {
    state.details.data = action.payload.visit;
    state.details.type = action.payload.type;
    state.details.step = action.payload.step;
  },
  [actions.PROCESS_VISIT_FAIL]: (state, action) => {
    state.details.errors.process = action.error;
    state.details.loading.process = false;
  },
  [actions.PROCESS_VISIT_SUCCESS]: state => {
    state.details.loading.process = false;
  },
  [actions.TRANSFER_VISIT]: state => {
    state.details.loading.transfer = true;
    state.details.errors.transfer = null;
  },
  [actions.TRANSFER_VISIT_FAIL]: (state, action) => {
    state.details.errors.transfer = action.error;
    state.details.loading.transfer = false;
  },
  [actions.TRANSFER_VISIT_SUCCESS]: state => {
    state.details.loading.transfer = false;
  },
  [actions.UPDATE_COUPON_CODE]: state => {
    state.coupon.error = null;
    state.coupon.loading = true;
  },
  [actions.UPDATE_COUPON_CODE_FAIL]: (state, action) => {
    state.coupon.error = action.error;
    state.coupon.loading = false;
  },
  [actions.UPDATE_COUPON_CODE_SUCCESS]: (state, action) => {
    state.details.data = action.payload;
    state.coupon.loading = false;
  },
  [actions.GET_VISIT_SUMMARY]: state => {
    state.summary.loading = true;
    state.summary.error = null;
  },
  [actions.GET_VISIT_SUMMARY_FAIL]: (state, action) => {
    state.summary.error = action.error;
    state.summary.loading = false;
  },
  [actions.GET_VISIT_SUMMARY_SUCCESS]: (state, action) => {
    state.summary.data = action.payload;
    state.summary.loading = false;
  },
  [actions.SEND_VISIT_SUMMARY_SUCCESS]: (state, action) => {
    state.summary.data = action.payload;
  },
  [actions.SETUP_WAITING_ROOM_ALERTS]: state => {
    state.alerts.error = null;
    state.alerts.loading = true;
  },
  [actions.SETUP_WAITING_ROOM_ALERTS_FAIL]: (state, action) => {
    state.alerts.error = action.error;
    state.alerts.loading = false;
  },
  [actions.SETUP_WAITING_ROOM_ALERTS_SUCCESS]: state => {
    state.alerts.error = null;
    state.alerts.loading = false;
  },
  [actions.ADD_CHAT_MESSAGE]: state => {
    state.chat.error = null;
    state.chat.loading = true;
  },
  [actions.ADD_CHAT_MESSAGE_FAIL]: (state, action) => {
    state.chat.error = action.error;
    state.chat.loading = false;
  },
  [actions.ADD_CHAT_MESSAGE_SUCCESS]: (state, action) => {
    state.chat.data = action.payload;
    state.chat.error = null;
    state.chat.loading = false;
  },
  [actions.START_WEB_RTC_VISIT]: state => {
    state.webRTCVideoConsole.error = null;
    state.webRTCVideoConsole.loading = true;
  },
  [actions.START_WEB_RTC_VISIT_FAIL]: (state, action) => {
    state.webRTCVideoConsole.error = action.error;
    state.webRTCVideoConsole.loading = false;
  },
  [actions.START_WEB_RTC_VISIT_SUCCESS]: (state, action) => {
    state.webRTCVideoConsole.data = action.payload;
    state.webRTCVideoConsole.error = null;
    state.webRTCVideoConsole.loading = false;
  },
  // Web Amwell Session Restore
  [storageActions.GET_STORAGE_SUCCESS]: (state, action) => {
    state.details.data = action.payload.visit;
    state.summary.data = action.payload.visitSummary;
  },
  [RESET_AMWELL_ACTIVITY]: () => visitInitialState
});
